import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useTranslation } from "react-i18next";
import { User, MapPin, Globe, Settings, Save, Plus, X, LogOut, Camera, Plane, Shield, Check, Clock, CalendarCheck, Star, BookmarkCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = ["tennis", "pickleball", "golf", "table-tennis", "ski", "padel", "squash", "badminton", "climbing"];
const SKILL_LEVELS = ["beginner", "intermediate", "advanced", "expert"];
const SPORT_LABELS = {
  "tennis": "Tennis", "pickleball": "Pickleball", "golf": "Golf", "table-tennis": "Table Tennis",
  "ski": "Skiing", "padel": "Padel", "squash": "Squash", "badminton": "Badminton", "climbing": "Climbing"
};

const GENDER_OPTIONS = [
  { value: "", label: "Prefer not to say" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non-binary", label: "Non-binary" },
];

const LANGUAGE_OPTIONS = [
  "English", "Spanish", "French", "Mandarin", "Korean",
  "Portuguese", "German", "Japanese", "Arabic", "Hindi"
];

const VERIFICATION_STATUS = {
  none: { label: "Not Submitted", icon: User, color: "text-gray-500", bg: "bg-[#252B3B]" },
  pending: { label: "Under Review", icon: Clock, color: "text-yellow-400", bg: "bg-yellow-400/10 border border-yellow-400/20" },
  verified: { label: "Verified", icon: Check, color: "text-green-400", bg: "bg-green-400/10 border border-green-400/20" },
  rejected: { label: "Not Approved", icon: X, color: "text-red-400", bg: "bg-red-400/10 border border-red-400/20" },
};

export default function AccountPage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    city: user?.city || "",
    play_intent: user?.play_intent || "both",
    has_equipment: user?.has_equipment ?? true,
    can_host: user?.can_host ?? false,
    sports: user?.sports || [],
    gender: user?.gender || "",
    languages: user?.languages || [],
    play_style: user?.play_style || "",
    preferred_courts: user?.preferred_courts || [],
    available_this_week: user?.available_this_week ?? false,
  });
  const [travelForm, setTravelForm] = useState({
    travel_enabled: user?.travel_enabled ?? false,
    travel_city: user?.travel_city || "",
    travel_start: user?.travel_start || "",
    travel_end: user?.travel_end || "",
  });
  const [travelSaving, setTravelSaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar_url || "");
  const [avatarError, setAvatarError] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Photo verification state
  const [photoVerifUrl, setPhotoVerifUrl] = useState("");
  const [submittingVerif, setSubmittingVerif] = useState(false);
  const [verifStatus, setVerifStatus] = useState(user?.photo_verification_status || "none");

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const max = 400;
        let { width, height } = img;
        if (width > max || height > max) {
          const ratio = Math.min(max / width, max / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const base64 = canvas.toDataURL("image/jpeg", 0.85);
        setAvatarPreview(base64);
        saveAvatar(base64);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const saveAvatar = async (base64) => {
    setAvatarUploading(true);
    setAvatarError("");
    try {
      const { data } = await axios.put(`${API}/users/me/avatar`, { avatar_url: base64 });
      updateUser(data);
    } catch (err) {
      // Previously only console.error, so a failed upload was completely
      // silent: the user picked a photo, the spinner stopped, and nothing
      // happened. The backend returns 413 for an inline image too large to store
      // without object storage, and that reason is worth showing.
      console.error("Avatar upload failed:", err);
      setAvatarError(
        err?.response?.data?.detail ||
          "Couldn't save that photo. Please try a different one."
      );
      setAvatarPreview(null);
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.put(`${API}/users/me`, form);
      updateUser(data);
      setSaved(true);
      showToast("Profile saved!");
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError("Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleTravelSave = async (e) => {
    e.preventDefault();
    setTravelSaving(true);
    try {
      const { data } = await axios.put(`${API}/users/me`, travelForm);
      updateUser(data);
      showToast(travelForm.travel_enabled ? `Travel mode active for ${travelForm.travel_city}!` : "Travel mode disabled.");
    } catch (err) {
      showToast("Failed to save travel settings.", "error");
    } finally {
      setTravelSaving(false);
    }
  };

  const addSport = () => {
    setForm((prev) => ({
      ...prev,
      sports: [...prev.sports, { sport: "tennis", skill_level: "intermediate", rating: null }],
    }));
  };

  const removeSport = (idx) => {
    setForm((prev) => ({
      ...prev,
      sports: prev.sports.filter((_, i) => i !== idx),
    }));
  };

  const updateSport = (idx, key, value) => {
    setForm((prev) => ({
      ...prev,
      sports: prev.sports.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    }));
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handlePhotoVerifSubmit = async () => {
    if (!photoVerifUrl) return;
    setSubmittingVerif(true);
    try {
      const { data } = await axios.post(`${API}/users/me/photo-verification`, { photo_url: photoVerifUrl }, { withCredentials: true });
      updateUser(data);
      setVerifStatus(data.photo_verification_status);
      setPhotoVerifUrl("");
      showToast("Photo submitted for verification!");
    } catch (err) {
      showToast("Failed to submit photo", "error");
    } finally {
      setSubmittingVerif(false);
    }
  };

  const toggleLanguage = (lang) => {
    setForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-heading font-bold text-brand-navy mb-2">Account Settings</h1>
        <p className="text-gray-500 text-sm mb-8">Update your profile, sports, and preferences.</p>

        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6">
          {/* Photo Upload */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
              <Camera size={16} className="text-brand-green" aria-hidden="true" /> Profile Photo
            </h2>
            <div className="flex items-center gap-5">
              <div className="relative flex-shrink-0">
                <div
                  className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-200 flex items-center justify-center cursor-pointer hover:border-brand-green/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  aria-label="Change profile photo"
                  onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                  data-testid="avatar-upload-trigger"
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <User size={28} className="text-slate-500" aria-hidden="true" />
                  )}
                  {avatarUploading && (
                    <div className="absolute inset-0 bg-black/60 rounded-full flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-lime border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-brand-orange text-white flex items-center justify-center shadow-md hover:bg-brand-orange-hover transition-colors"
                  aria-label="Upload photo"
                  data-testid="avatar-upload-button"
                >
                  <Camera size={13} aria-hidden="true" />
                </button>
              </div>
              <div>
                <p className="text-sm text-brand-navy font-medium">Upload a photo</p>
                <p className="text-xs text-gray-500 mt-0.5">JPG, PNG or GIF. Auto-resized to 400×400px.</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 text-xs text-brand-green hover:underline"
                  data-testid="avatar-choose-file"
                >
                  Choose file
                </button>
                {/* A failed upload used to be invisible - the spinner stopped and
                    nothing was shown. Say what went wrong. */}
                {avatarError && (
                  <p
                    className="text-xs text-red-600 mt-2"
                    role="alert"
                    data-testid="avatar-error"
                  >
                    {avatarError}
                  </p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="sr-only"
                aria-label="Profile photo file input"
                data-testid="avatar-file-input"
              />
            </div>
          </div>

          {/* Basic Info */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
              <User size={16} className="text-brand-green" /> {t("account.basicInfo")}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="AccountfullName" className="block text-sm font-medium text-brand-navy mb-1">{t("account.fullName")}</label><input id="AccountfullName"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40"
                  data-testid="account-name-input"
                />
              </div>
              <div>
                <label htmlFor="Accountbio" className="block text-sm font-medium text-brand-navy mb-1">{t("account.bio")}</label><textarea id="Accountbio"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  rows={3}
                  placeholder="Tell potential partners about yourself and your playing style..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 resize-none"
                  data-testid="account-bio-input"
                />
              </div>
              <div>
                <label htmlFor="Accountcity" className="block text-sm font-medium text-brand-navy mb-1 flex items-center gap-1">
                  <MapPin size={13} /> {t("account.city")}
                </label><input id="Accountcity"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Fort Worth, TX"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40"
                  data-testid="account-city-input"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-brand-navy mb-2">{t("account.gender")}</label>
                <div className="flex flex-wrap gap-2">
                  {GENDER_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setForm({ ...form, gender: value })}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${form.gender === value ? "bg-brand-orange text-white font-medium" : "bg-gray-100 text-gray-500 hover:text-brand-navy"}`}
                      data-testid={`gender-option-${value || "none"}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-brand-navy mb-2 flex items-center gap-1">
                  <Globe size={13} /> {t("account.languages")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGE_OPTIONS.map(lang => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => toggleLanguage(lang)}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${form.languages.includes(lang) ? "bg-brand-navy text-white font-medium" : "bg-gray-100 text-gray-500 hover:text-brand-navy"} dark-section`}
                      data-testid={`lang-option-${lang.toLowerCase()}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sports */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-brand-navy flex items-center gap-2">
                <Globe size={16} className="text-brand-green" /> Your Sports
              </h2>
              <button type="button" onClick={addSport} className="flex items-center gap-1 text-xs text-brand-green hover:underline">
                <Plus size={13} /> Add Sport
              </button>
            </div>
            <div className="space-y-3">
              {form.sports.map((s, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-[#252B3B] rounded-lg p-3 dark-section">
                  <select
                    value={s.sport}
                    onChange={(e) => updateSport(idx, "sport", e.target.value)}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-brand-navy text-xs focus:outline-none flex-1"
                  >
                    {SPORTS.map((sp) => <option key={sp} value={sp}>{SPORT_LABELS[sp]}</option>)}
                  </select>
                  <select
                    value={s.skill_level}
                    onChange={(e) => updateSport(idx, "skill_level", e.target.value)}
                    className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-brand-navy text-xs focus:outline-none"
                  >
                    {SKILL_LEVELS.map((sl) => <option key={sl} value={sl} className="capitalize">{sl}</option>)}
                  </select>
                  <button type="button" onClick={() => removeSport(idx)} className="text-slate-500 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ))}
              {form.sports.length === 0 && (
                <p className="text-sm text-slate-500">No sports added yet. Click "Add Sport" to get started.</p>
              )}
            </div>
          </div>

          {/* Preferences */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-bold text-brand-navy mb-4 flex items-center gap-2">
              <Settings size={16} className="text-brand-green" /> Preferences
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-brand-navy mb-2">Play Intent</label>
                <div className="flex gap-2">
                  {["casual", "competitive", "both"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm({ ...form, play_intent: v })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${form.play_intent === v ? "bg-brand-orange text-navy" : "bg-gray-100 text-gray-500 hover:text-brand-navy"}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-300" id="equipment-label">Has own equipment</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.has_equipment}
                  aria-labelledby="equipment-label"
                  onClick={() => setForm({ ...form, has_equipment: !form.has_equipment })}
                  className={`w-10 h-6 rounded-full transition-colors ${form.has_equipment ? "bg-brand-green" : "bg-gray-200"} relative`}
                  data-testid="equipment-toggle"
                >
                  <span className="sr-only">{form.has_equipment ? "Yes" : "No"}</span>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${form.has_equipment ? "right-1" : "left-1"}`} aria-hidden="true" />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-300" id="host-label">Can host at home venue</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.can_host}
                  aria-labelledby="host-label"
                  onClick={() => setForm({ ...form, can_host: !form.can_host })}
                  className={`w-10 h-6 rounded-full transition-colors ${form.can_host ? "bg-brand-green" : "bg-gray-200"} relative`}
                  data-testid="host-toggle"
                >
                  <span className="sr-only">{form.can_host ? "Yes" : "No"}</span>
                  <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${form.can_host ? "right-1" : "left-1"}`} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-brand-orange text-navy font-bold rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50"
              data-testid="account-save-button"
            >
              <Save size={16} /> {saved ? t("account.saved") : saving ? t("account.saving") : t("account.saveChanges")}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-3 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
              data-testid="account-logout-button"
            >
              <LogOut size={16} /> {t("account.signOut")}
            </button>
          </div>
        </form>

        {/* V1: Weekly Availability + Play Style */}
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="v1-profile-section">
          <h2 className="font-bold text-brand-navy mb-1 flex items-center gap-2">
            <CalendarCheck size={16} className="text-brand-green" /> Weekly Availability & Play Style
          </h2>
          <p className="text-xs text-gray-500 mb-5">Let other players know you're available to play this week and share your play style.</p>

          {/* Available this week toggle */}
          <div className="flex items-center justify-between mb-5 p-3 bg-green-50 border border-green-200/60 rounded-xl">
            <div>
              <p className="text-sm font-medium text-gray-700">Available This Week</p>
              <p className="text-xs text-gray-500 mt-0.5">Appears in "Available This Week" filter</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.available_this_week}
              onClick={async () => {
                const newVal = !form.available_this_week;
                setForm(f => ({ ...f, available_this_week: newVal }));
                try {
                  await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, { available_this_week: newVal }, { withCredentials: true });
                  showToast(newVal ? "You're showing as available this week!" : "Availability hidden.");
                } catch { showToast("Failed to update availability."); }
              }}
              className={`w-11 h-6 rounded-full transition-colors relative ${form.available_this_week ? "bg-brand-green" : "bg-gray-200"}`}
              data-testid="available-this-week-account-toggle"
            >
              <span className="sr-only">Available this week</span>
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform ${form.available_this_week ? "right-1" : "left-1"}`} />
            </button>
          </div>

          {/* Play Style */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
              <Star size={13} className="text-brand-green" /> Play Style
            </label>
            <div className="flex flex-wrap gap-2">
              {["aggressive", "balanced", "defensive", "competitive", "social"].map(style => (
                <button
                  key={style}
                  type="button"
                  onClick={async () => {
                    const newStyle = form.play_style === style ? "" : style;
                    setForm(f => ({ ...f, play_style: newStyle }));
                    try {
                      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/me`, { play_style: newStyle }, { withCredentials: true });
                    } catch {}
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors ${
                    form.play_style === style
                      ? "bg-brand-green/10 border-brand-green text-brand-green"
                      : "bg-gray-50 border-gray-200 text-gray-500 hover:border-brand-green/40"
                  }`}
                  data-testid={`play-style-${style}`}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>

          {/* Preferred Courts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
              <BookmarkCheck size={13} className="text-brand-green" /> Preferred Courts
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(form.preferred_courts || []).map(court => (
                <span key={court} className="inline-flex items-center gap-1 px-2.5 py-1 bg-brand-green/10 border border-brand-green/30 rounded-lg text-xs text-brand-green">
                  {court}
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, preferred_courts: f.preferred_courts.filter(c => c !== court) }))}
                    className="ml-0.5 hover:text-red-400"
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g. Bayard H. Friedman Tennis Center"
                className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green/30"
                data-testid="preferred-court-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const val = e.target.value.trim();
                    if (val && !(form.preferred_courts || []).includes(val)) {
                      setForm(f => ({ ...f, preferred_courts: [...(f.preferred_courts || []), val] }));
                    }
                    e.target.value = "";
                  }
                }}
              />
              <span className="text-xs text-gray-400 self-center">↵ Enter</span>
            </div>
          </div>
        </div>

        {/* Photo Verification */}
        <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="photo-verification-section">
          <h2 className="font-bold text-brand-navy mb-1 flex items-center gap-2">
            <Shield size={16} className="text-brand-orange" aria-hidden="true" /> {t("account.photoVerification")}
          </h2>
          <p className="text-xs text-gray-500 mb-4">{t("account.photoVerificationDesc")}</p>

          {/* Current status */}
          {(() => {
            const cfg = VERIFICATION_STATUS[verifStatus] || VERIFICATION_STATUS.none;
            const Icon = cfg.icon;
            return (
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm mb-4 ${cfg.bg}`}>
                <Icon size={14} className={cfg.color} />
                <span className={cfg.color}>{t(`account.verificationStatus.${verifStatus}`) || cfg.label}</span>
              </div>
            );
          })()}

          {/* Submit form (only shown if not verified) */}
          {verifStatus !== "verified" && (
            <div className="flex gap-2">
              <input
                type="url"
                value={photoVerifUrl}
                onChange={e => setPhotoVerifUrl(e.target.value)}
                placeholder="https://... (link to your photo)"
                className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-brand-navy text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 placeholder-gray-400"
                data-testid="photo-verif-url-input"
              />
              <button
                type="button"
                onClick={handlePhotoVerifSubmit}
                disabled={!photoVerifUrl || submittingVerif}
                className="px-4 py-2 bg-brand-orange text-navy font-bold text-sm rounded-lg hover:bg-brand-orange-hover transition-colors disabled:opacity-50 whitespace-nowrap"
                data-testid="photo-verif-submit-btn"
              >
                {submittingVerif ? "Submitting..." : t("account.submitForVerification")}
              </button>
            </div>
          )}
        </div>

        {/* Travel Mode — separate card outside the profile form */}
        <form onSubmit={handleTravelSave} className="mt-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-testid="travel-mode-section">
          <h2 className="font-bold text-brand-navy mb-1 flex items-center gap-2">
            <Plane size={16} className="text-brand-green" aria-hidden="true" /> Travel Mode
          </h2>
          <p className="text-xs text-gray-500 mb-5">Find partners in a destination city during your travel dates.</p>

          <div className="flex items-center justify-between mb-5">
            <div>
              <span className="text-sm text-slate-300 font-medium" id="travel-toggle-label">Enable Travel Mode</span>
              {travelForm.travel_enabled && travelForm.travel_city && (
                <p className="text-xs text-brand-green mt-0.5">Active — searching in {travelForm.travel_city}</p>
              )}
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={travelForm.travel_enabled}
              aria-labelledby="travel-toggle-label"
              onClick={() => setTravelForm((p) => ({ ...p, travel_enabled: !p.travel_enabled }))}
              className={`w-10 h-6 rounded-full transition-colors ${travelForm.travel_enabled ? "bg-brand-green" : "bg-gray-200"} relative flex-shrink-0`}
              data-testid="travel-mode-toggle"
            >
              <span className="sr-only">{travelForm.travel_enabled ? "On" : "Off"}</span>
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform duration-200 ${travelForm.travel_enabled ? "right-1" : "left-1"}`} aria-hidden="true" />
            </button>
          </div>

          <div className={`space-y-4 transition-opacity ${travelForm.travel_enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
            <div>
              <label htmlFor="travel-city" className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Destination City</label>
              <input
                id="travel-city"
                type="text"
                value={travelForm.travel_city}
                onChange={(e) => setTravelForm((p) => ({ ...p, travel_city: e.target.value }))}
                placeholder="Denver, CO"
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-brand-navy placeholder-gray-400 focus:outline-none focus:border-brand-green/50"
                data-testid="travel-city-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="travel-start" className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Arriving</label>
                <input
                  id="travel-start"
                  type="date"
                  value={travelForm.travel_start}
                  onChange={(e) => setTravelForm((p) => ({ ...p, travel_start: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-brand-navy focus:outline-none focus:border-brand-green/50"
                  data-testid="travel-start-input"
                />
              </div>
              <div>
                <label htmlFor="travel-end" className="block text-xs text-gray-500 uppercase tracking-wider mb-1.5">Departing</label>
                <input
                  id="travel-end"
                  type="date"
                  value={travelForm.travel_end}
                  onChange={(e) => setTravelForm((p) => ({ ...p, travel_end: e.target.value }))}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-brand-navy focus:outline-none focus:border-brand-green/50"
                  data-testid="travel-end-input"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={travelSaving}
            className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 bg-brand-orange text-navy font-bold text-sm rounded-lg hover:bg-lime-hover transition-colors disabled:opacity-50"
            data-testid="travel-save-button"
          >
            <Plane size={14} aria-hidden="true" /> {travelSaving ? "Saving..." : "Save Travel Settings"}
          </button>
        </form>
      </div>
    </div>
  );
}
